import React, { useState, useEffect } from "react";
import { Box, IconButton, Typography, useTheme, useMediaQuery } from "@mui/material";
import { Delete, Edit, Check, Close, ColorLens } from "@mui/icons-material";
import { red, green, orange } from "@mui/material/colors";
import ICoverageCardProps from "../../interfaces/ICoverageCardProps";

export const CoverageCard: React.FC<ICoverageCardProps> = ({ area, isSelected, onSelect, onColorChange, colorPoint, onDelete, onOpenEditMode }) => {
  const [isEditingColor, setIsEditingColor] = useState(false);
  const [tempColor, setTempColor] = useState(colorPoint);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  useEffect(() => {
    if (!isEditingColor) setTempColor(colorPoint);
  }, [colorPoint, isEditingColor]);

  const handleHueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const hue = e.target.value;
    const newColor = `hsl(${hue}, 80%, 50%)`;
    setTempColor(newColor);
  };

  const handleSaveColor = (e: React.MouseEvent) => {
    e.stopPropagation();
    onColorChange(area.id, tempColor);
    setIsEditingColor(false);
  };

  const handleCancel = (e: React.MouseEvent) => {
    e.stopPropagation();
    setTempColor(colorPoint);
    setIsEditingColor(false);
  };

  return (
    <Box
      onClick={onSelect}
      sx={{
        display: "flex",
        flexDirection: "column",
        // Desktop: 12px | Mobile: 8px
        padding: isMobile ? "8px 10px" : "12px",
        border: isSelected ? `2px solid ${tempColor}` : "1px solid var(--fx-surface-border, #E2E8F0)",
        borderRadius: "12px",
        cursor: "pointer",
        transition: "all 0.2s ease-in-out",
        bgcolor: isSelected ? `${tempColor}08` : "var(--fx-surface-base, #FFFFFF)",
        boxShadow: isSelected ? `0 4px 10px ${tempColor}20` : "none",
        position: "relative",
        // Desktop: 0.5 | Mobile: 0.2
        gap: isMobile ? 0.2 : 0.5,
      }}
    >
      {/* HEADER */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Box display="flex" alignItems="center" sx={{ flex: 1, overflow: "hidden" }}>
          <Box
            sx={{
              // Desktop: 12px | Mobile: 10px
              width: isMobile ? "10px" : "12px",
              height: isMobile ? "10px" : "12px",
              borderRadius: "50%",
              background: tempColor,
              mr: 1,
              flexShrink: 0,
            }}
          />
          <Typography
            sx={{
              // Desktop: 14px | Mobile: 13px
              fontSize: isMobile ? "13px" : "14px",
              fontWeight: 600,
              color: "var(--fx-surface-text, #0F172A)",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {area.name}
          </Typography>
        </Box>

        <Box sx={{ display: "flex" }}>
          {isEditingColor ? (
            <>
              <IconButton size="small" onClick={handleSaveColor} sx={{ color: green[600], p: isMobile ? 0.3 : 0.5 }}>
                <Check sx={{ fontSize: isMobile ? 18 : 20 }} />
              </IconButton>
              <IconButton size="small" onClick={handleCancel} sx={{ color: red[400], p: isMobile ? 0.3 : 0.5 }}>
                <Close sx={{ fontSize: isMobile ? 18 : 20 }} />
              </IconButton>
            </>
          ) : (
            <>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  setIsEditingColor(true);
                }}
                sx={{ p: isMobile ? 0.3 : 0.5 }}
              >
                <ColorLens sx={{ fontSize: isMobile ? 16 : 18 }} />
              </IconButton>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  onOpenEditMode(area);
                }}
                sx={{ p: isMobile ? 0.3 : 0.5 }}
              >
                <Edit sx={{ fontSize: isMobile ? 16 : 18 }} />
              </IconButton>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete(area.id);
                }}
                sx={{ p: isMobile ? 0.3 : 0.5 }}
              >
                <Delete sx={{ fontSize: isMobile ? 16 : 18, color: red[300] }} />
              </IconButton>
            </>
          )}
        </Box>
      </Box>

      {/* DIRECCIÓN */}
      <Typography
        sx={{
          fontSize: isMobile ? "10px" : "12px",
          color: "var(--fx-surface-textsecondary, #64748B)",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {area.address}
      </Typography>

      <Typography sx={{ fontSize: isMobile ? "11px" : "12px", color: "var(--fx-surface-textsecondary, #64748B)" }}>
        Radius: <span style={{ fontWeight: 600 }}>{area.milesOfCoverageArea || 0} miles</span>
      </Typography>

      {isEditingColor && (
        <Box
          sx={{
            width: "100%",
            mt: isMobile ? 0.5 : 1,
            display: "flex",
            alignItems: "center",
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <input
            type="range"
            min="0"
            max="360"
            onChange={handleHueChange}
            style={{
              width: "100%",
              // Desktop: 8px | Mobile: 5px
              height: isMobile ? "5px" : "8px",
              borderRadius: "5px",
              appearance: "none",
              background: "linear-gradient(to right, #ff0000 0%, #ffff00 17%, #00ff00 33%, #00ffff 50%, #0000ff 67%, #ff00ff 83%, #ff0000 100%)",
              outline: "none",
            }}
            className="hue-slider-mini"
          />
          <style>{`
            .hue-slider-mini::-webkit-slider-thumb {
              -webkit-appearance: none;
              /* Desktop: 18px | Mobile: 14px */
              width: ${isMobile ? "14px" : "18px"};
              height: ${isMobile ? "14px" : "18px"};
              border-radius: 50%;
              background: white;
              border: 2px solid ${tempColor};
              box-shadow: 0 1px 3px rgba(0,0,0,0.2);
              cursor: pointer;
            }
          `}</style>
        </Box>
      )}
    </Box>
  );
};
